import { Link } from 'react-router-dom';

interface ArticleCardProps {
  slug: string;
  title: string;
  excerpt: string;
  imageUrl: string;
  category: string;
  categoryName: string;
  publishedAt: string;
  views: number;
  comments: number;
  isHot?: boolean;
  variant?: 'default' | 'featured' | 'compact';
}

export default function ArticleCard({
  slug,
  title,
  excerpt,
  imageUrl,
  category,
  categoryName,
  publishedAt,
  views,
  comments,
  isHot,
  variant = 'default',
}: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group block rounded-xl overflow-hidden bg-background-100 border border-background-200/30 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
      >
        <div className="aspect-[16/9] overflow-hidden relative">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          <div className="absolute top-3 left-3">
            <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
              {categoryName}
            </span>
          </div>
          {isHot && (
            <div className="absolute top-3 right-3">
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-accent-500/80 text-background-50 text-xs font-medium">
                <i className="ri-fire-fill text-xs"></i>
                热门
              </span>
            </div>
          )}
        </div>
        <div className="p-4 md:p-5">
          <h3 className="text-base md:text-lg font-semibold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2">
            {title}
          </h3>
          <p className="text-sm text-foreground-500 line-clamp-2 mb-3 leading-relaxed">
            {excerpt}
          </p>
          <div className="flex items-center gap-3 text-xs text-foreground-500">
            <span className="flex items-center gap-1">
              <i className="ri-time-line"></i>
              {new Date(publishedAt).toLocaleDateString('zh-CN', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-eye-line"></i>
              {(views / 10000).toFixed(1)}万
            </span>
            <span className="flex items-center gap-1">
              <i className="ri-chat-3-line"></i>
              {comments}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${slug}`}
        className="group flex gap-3 md:gap-4 p-3 rounded-lg hover:bg-background-100 transition-colors duration-200 cursor-pointer"
      >
        <div className="w-20 h-20 md:w-24 md:h-24 flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={imageUrl}
            alt={title}
            className="w-full h-full object-cover object-top"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-800 group-hover:text-primary-400 transition-colors line-clamp-2 mb-1.5 leading-snug">
            {title}
          </h4>
          <div className="flex items-center gap-2 text-xs text-foreground-500">
            <span className="px-1.5 py-0.5 rounded bg-primary-500/10 text-primary-400 text-[10px] font-medium">
              {categoryName}
            </span>
            <span>
              {new Date(publishedAt).toLocaleDateString('zh-CN', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${slug}`}
      className="group block rounded-lg overflow-hidden bg-background-100 border border-background-200/30 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={imageUrl}
          alt={title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
      </div>
      <div className="p-3 md:p-4">
        <div className="flex items-center gap-2 mb-2">
          <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-primary-500/10 text-primary-400">
            {categoryName}
          </span>
          {isHot && (
            <span className="inline-flex items-center gap-0.5 text-[10px] text-accent-500">
              <i className="ri-fire-fill"></i>
              热门
            </span>
          )}
        </div>
        <h3 className="text-sm md:text-base font-semibold text-foreground-900 group-hover:text-primary-400 transition-colors line-clamp-2 mb-2 leading-snug">
          {title}
        </h3>
        <div className="flex items-center gap-2 text-[10px] md:text-xs text-foreground-500">
          <span>
            {new Date(publishedAt).toLocaleDateString('zh-CN', {
              month: 'short',
              day: 'numeric',
            })}
          </span>
          <span>{(views / 10000).toFixed(1)}万阅读</span>
        </div>
      </div>
    </Link>
  );
}